/**
 * The playback clock.
 *
 * The authoritative time source for everything audible is `AudioContext.
 * currentTime`, a monotonic hardware-derived clock in the audio thread. React
 * render timing, `setInterval` and `setTimeout` are all subject to jitter,
 * throttling in background tabs, and garbage-collection pauses; none of them is
 * ever used to decide *when* a sound happens. They are only ever used to decide
 * *when to think about* scheduling, which is a different and forgiving job.
 *
 * A `Timeline` maps context time onto project time. Because looping makes that
 * mapping discontinuous, it is stored as a queue of anchors: each anchor says
 * "at this context time, the project is at this position". The scheduler pushes
 * anchors slightly ahead of time — a loop wrap is scheduled before it is heard —
 * so a UI polling the position at 60fps never sees a stale or overshooting
 * playhead, even though the scheduler only wakes 40 times a second.
 */

export interface TimelineAnchor {
  /** `AudioContext.currentTime` at which this segment begins. */
  contextTime: number;
  /** Project position, in seconds, at that instant. */
  projectSeconds: number;
  /** Project seconds at which this segment ends (a loop wrap or the stop point). */
  endProjectSeconds: number;
}

export class Timeline {
  private anchors: TimelineAnchor[] = [];

  reset(anchor: TimelineAnchor): void {
    this.anchors = [anchor];
  }

  push(anchor: TimelineAnchor): void {
    this.anchors.push(anchor);
    // Keep the queue small; anything older than the current segment is dead.
    if (this.anchors.length > 64) this.anchors.splice(0, this.anchors.length - 64);
  }

  get isEmpty(): boolean {
    return this.anchors.length === 0;
  }

  /** The anchor governing `contextTime`, or null before playback starts. */
  anchorAt(contextTime: number): TimelineAnchor | null {
    let found: TimelineAnchor | null = null;
    for (const anchor of this.anchors) {
      if (anchor.contextTime <= contextTime) found = anchor;
      else break;
    }
    return found ?? this.anchors[0] ?? null;
  }

  /** Project position in seconds at a given context time. */
  projectSecondsAt(contextTime: number): number {
    const anchor = this.anchorAt(contextTime);
    if (!anchor) return 0;
    const raw = anchor.projectSeconds + Math.max(0, contextTime - anchor.contextTime);
    return Math.min(raw, anchor.endProjectSeconds);
  }

  /**
   * Context time at which a project position will be reached, within the
   * segment that is currently scheduling. Returns null when the position lies
   * outside every known segment (i.e. it has not been scheduled yet).
   */
  contextTimeFor(projectSeconds: number): number | null {
    for (let i = this.anchors.length - 1; i >= 0; i--) {
      const anchor = this.anchors[i];
      if (projectSeconds >= anchor.projectSeconds && projectSeconds <= anchor.endProjectSeconds) {
        return anchor.contextTime + (projectSeconds - anchor.projectSeconds);
      }
    }
    return null;
  }

  /** Drops anchors that ended before `contextTime`, keeping the active one. */
  prune(contextTime: number): void {
    let keepFrom = 0;
    for (let i = 0; i < this.anchors.length; i++) {
      if (this.anchors[i].contextTime <= contextTime) keepFrom = i;
      else break;
    }
    if (keepFrom > 0) this.anchors.splice(0, keepFrom);
  }

  clear(): void {
    this.anchors = [];
  }
}

/**
 * Interval pump.
 *
 * Wakes the scheduler often enough that it always has a look-ahead window to
 * fill. Uses a Worker-hosted interval when one is available, because browsers
 * clamp main-thread timers to once per second in background tabs — which would
 * starve the scheduler and produce audible gaps while the user is in another
 * tab. The pump never determines *when* audio plays, only how often we top up
 * the schedule.
 */
export class SchedulerPump {
  private worker: Worker | null = null;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private callback: (() => void) | null = null;

  constructor(private readonly intervalMs: number = 25) {}

  start(callback: () => void): void {
    this.stop();
    this.callback = callback;

    const source = `let id=null;self.onmessage=(e)=>{if(e.data&&e.data.type==='start'){clearInterval(id);id=setInterval(()=>self.postMessage('tick'),e.data.interval);}else if(e.data&&e.data.type==='stop'){clearInterval(id);id=null;}};`;
    try {
      const url = URL.createObjectURL(new Blob([source], { type: 'text/javascript' }));
      this.worker = new Worker(url);
      URL.revokeObjectURL(url);
      this.worker.onmessage = () => this.callback?.();
      this.worker.postMessage({ type: 'start', interval: this.intervalMs });
      return;
    } catch {
      this.worker = null;
    }

    // Workers can be unavailable under strict CSP; a main-thread timer still
    // works, it is just throttled when the tab is hidden.
    this.intervalId = setInterval(() => this.callback?.(), this.intervalMs);
  }

  stop(): void {
    if (this.worker) {
      this.worker.postMessage({ type: 'stop' });
      this.worker.terminate();
      this.worker = null;
    }
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    this.callback = null;
  }
}
